/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef, useEffect } from 'react'
import { camelCase } from 'camel-case'
import { format } from 'date-fns'

import { ErrorBoundary } from 'react-error-boundary'

import Stack from '@mui/material/Stack'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Toolbar from '@mui/material/Toolbar'
import { ResizableBox } from 'react-resizable'
import Draggable from 'react-draggable'
import UserIcon from 'src/components/ui/UserIcon'
import { createPortal } from 'react-dom'
import { Badge, Chip, TextField } from '@mui/material'

import { Markdown, MarkdownProps } from '@react-email/components'
import {
  IMessage,
  useAssistantAction,
  useAssistantSelect,
} from 'src/hooks/useAssistant'

interface Message {
  id: string
  text: string
  isUser: boolean
  timestamp: Date
  isStreaming?: boolean
  metadata?: {
    responseTime?: number
    toolsCalled?: string[]
  }
}

const MarkdownStyles: MarkdownProps['markdownCustomStyles'] = {
  h1: { fontSize: '24px', fontWeight: 600, margin: '16px 0px 8px 0px' },
  h2: { fontSize: '22px', fontWeight: 600, margin: '16px 0px 8px 0px' },
  h3: { fontSize: '20px', fontWeight: 600, margin: '16px 0px 8px 0px' },
  h4: { fontSize: '18px', fontWeight: 600, margin: '12px 0px 4px 0px' },
  h5: { fontSize: '16px', fontWeight: 600, margin: '12px 0px 4px 0px' },
  h6: { fontSize: '14px', fontWeight: 600, margin: '12px 0px 4px 0px' },
  codeBlock: { background: 'transparent' },
  p: {
    padding: '6px 0px',
  },
  ul: { padding: '6px 0px 6px 23px' },
  ol: { padding: '6px 0px 6px 23px' },
  li: { padding: '4px 0px' },
  th: {
    textAlign: 'left',
  },
}
// Типы для токенов
type Token =
  | { type: 'char'; value: string }
  | { type: 'line'; value: string }
  | { type: 'open'; el: HTMLElement }
  | { type: 'close' }

const RenderTyping = (props: {
  text: string
  children: React.ReactNode
  mode?: 'char' | 'line' // <== новый проп
}) => {
  const { text, mode = 'char' } = props
  const container = useRef<HTMLDivElement>(null)
  const render = useRef<HTMLDivElement>(null)
  const tokens = useRef<Token[]>([])

  function flatten(node: Node): Token[] {
    const out: Token[] = []
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const content = child.textContent || ''
        if (mode === 'char') {
          for (const ch of content) {
            out.push({ type: 'char', value: ch })
          }
        } else if (mode === 'line' && content.trim().length > 0) {
          out.push({ type: 'line', value: content })
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const clone = child.cloneNode(false) as HTMLElement
        out.push({ type: 'open', el: clone })
        out.push(...flatten(child))
        out.push({ type: 'close' })
      }
    })

    return out
  }

  React.useEffect(() => {
    if (render.current && container.current) {
      tokens.current = flatten(container.current as Node)
    }
  }, [text, mode])

  React.useEffect(() => {
    if (render.current && container.current) {
      const stack: HTMLElement[] = [render.current]
      let i = 0
      const speed = 10 // скорость для строк медленнее
      let lastTime = 0

      function type(time: number) {
        if (i >= tokens.current.length || !container?.current) return
        if (time - lastTime >= speed) {
          const token = tokens.current[i]

          if (token.type === 'char') {
            stack[stack.length - 1].append(token.value)
          } else if (token.type === 'line') {
            console.debug(stack[stack.length - 1], token)
            stack[stack.length - 1].append(token.value)
            // stack[stack.length - 1].appendChild(document.createElement('br'))
          } else if (token.type === 'open') {
            const el = token.el
            stack[stack.length - 1].appendChild(el)
            stack.push(el)
          } else if (token.type === 'close') {
            stack.pop()
          }

          i++
          lastTime = time
        }
        requestAnimationFrame(type)
      }

      requestAnimationFrame(type)
    }
  }, [text, mode])

  return (
    <div>
      <div ref={render}></div>
      <div ref={container} style={{ display: 'none' }}>
        {props.children}
      </div>
    </div>
  )
}

const StreamMessage = (props: { threadId: string }) => {
  const { threadId } = props
  const streamMessages = useAssistantSelect.streamMessages()
  const currentMessage = streamMessages?.[threadId]

  // const [text, setText] = React.useState(streamMessage?.text || message.text)

  // React.useEffect(() => {
  //   if (streamMessage?.text) {
  //     setText(streamMessage.text)
  //   }
  // }, [streamMessage?.text])
  if (!currentMessage) {
    return <></>
  }

  if (!currentMessage.content) {
    return (
      <Stack
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        width={50}
        height={20}
      >
        <Typography color={'primary'}>
          <UserIcon
            icon="eos-icons:three-dots-loading"
            fontSize={42}
          ></UserIcon>
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack
      justifyContent={'center'}
      width={'100%'}
      px={3}
      alignItems={'flex-start'}
    >
      <Stack
        borderRadius={'5px'}
        py={2}
        px={2}
        sx={{
          backgroundColor: 'white',
        }}
        spacing={1}
        maxWidth={'100%'}
        minWidth={'100px'}
      >
        <Typography variant="body2">
          <Markdown
            children={currentMessage.content}
            markdownCustomStyles={MarkdownStyles}
          />
        </Typography>
      </Stack>
    </Stack>
  )
}

const SendButton = (props: {
  threadId: string
  disabled: boolean
  onSendMessage: () => void
}) => {
  const { threadId, disabled, onSendMessage } = props
  const streamMessageCancel = useAssistantAction.streamMessageCancel()

  const streamMessages = useAssistantSelect.streamMessages()
  const currentMessage = streamMessages?.[threadId]
  if (currentMessage) {
    return (
      <IconButton
        sx={{
          position: 'absolute',
          right: '5px',
          top: 'calc(57% - 21px)',
          height: '42px',
        }}
        onClick={() => streamMessageCancel({ threadId })}
        color="secondary"
      >
        <Typography color={'secondary'}>
          <UserIcon fontSize={26} icon="ic:round-stop"></UserIcon>
        </Typography>
      </IconButton>
    )
  }

  return (
    <IconButton
      sx={{
        position: 'absolute',
        right: '5px',
        top: 'calc(57% - 21px)',
        height: '42px',
      }}
      onClick={onSendMessage}
      disabled={disabled}
      color="primary"
    >
      <Typography color={disabled ? 'secondary' : 'primary'}>
        <UserIcon fontSize={26} icon="iconamoon:send-light"></UserIcon>
      </Typography>
    </IconButton>
  )
}
export const AssinstantChat = () => {
  const assinstantWindow = useAssistantSelect.window()
  const sendMessage = useAssistantAction.sendMessage()
  const getMemoryThreadsPaginated =
    useAssistantAction.getMemoryThreadsPaginated()
  const getMemoryThreadsItemMessages =
    useAssistantAction.getMemoryThreadsItemMessages()
  const threads = useAssistantSelect.threads()
  const [defaultThread] = threads.threads
  const selectedThread = defaultThread

  React.useEffect(() => {
    if (threads.threads.length === 0) {
      getMemoryThreadsPaginated({
        page: 0,
        perPage: 1,
        sortDirection: 'ASC',
      })
    }
  }, [threads.threads.length])
  React.useEffect(() => {
    if (selectedThread?.id) {
      getMemoryThreadsItemMessages({
        threadId: selectedThread.id,
        limit: 20,
      })
    }
  }, [selectedThread?.id])

  const closeWidow = useAssistantAction.closeWidow()
  const updateWindow = useAssistantAction.updateWindow()

  const [inputValue, setInputValue] = useState('')

  const [connectionStatus, setConnectionStatus] = useState<
    'connected' | 'connecting' | 'disconnected'
  >('connected')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messages = selectedThread?.messages?.items || []

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages.length])

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !selectedThread) return

    const userMessage = inputValue.trim()

    setInputValue('')
    sendMessage({ threadId: selectedThread?.id, text: userMessage })
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      handleSendMessage()
    }
  }

  return (
    <Stack
      height={'100%'}
      direction={'column'}
      width={'100%'}
      position={'relative'}
    >
      <Toolbar
        className={
          !assinstantWindow.rightSideBar ? 'assistant-draggable-handle' : ''
        }
        variant="dense"
        sx={{
          position: 'absolute',
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(5px)',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          top: 0,
          width: '100%',
          borderBottom: '1px solid',
          cursor: !assinstantWindow.rightSideBar ? 'grab' : 'default',
          borderColor: 'rgba(0,0,0,0.08)',
        }}
      >
        <Typography color="primary" sx={{ userSelect: 'none' }} pr={1} pt={1}>
          <UserIcon icon="mingcute:ai-fill" fontSize={18}></UserIcon>
        </Typography>
        <Typography sx={{ userSelect: 'none', fontSize: 16, fontWeight: 400 }}>
          Assistant
        </Typography>
        <Stack
          flex={1}
          flexDirection={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
        >
          {assinstantWindow.rightSideBar === true && (
            <IconButton
              size="small"
              sx={{ p: 1 }}
              onClick={() => updateWindow({ rightSideBar: false })}
            >
              <UserIcon
                icon="material-symbols:select-window-2-outline"
                fontSize="16px"
              />
            </IconButton>
          )}
          {assinstantWindow.rightSideBar === false && (
            <IconButton
              size="small"
              sx={{ p: 1 }}
              onClick={() => updateWindow({ rightSideBar: true })}
            >
              <UserIcon
                icon="streamline-flex:layout-right-sidebar-remix"
                fontSize="16px"
              />
            </IconButton>
          )}

          <IconButton size="small" sx={{ p: 1 }} onClick={() => closeWidow()}>
            <UserIcon icon="tabler:x" fontSize="16px" />
          </IconButton>
        </Stack>
      </Toolbar>
      <Stack
        display={'flex'}
        direction={'column-reverse'}
        spacing={3}
        pt={'62px'}
        pb={'48px'}
        mb={'22px'}
        height={'100%'}
        maxHeight={'100%'}
        sx={{ overflowY: 'auto' }}
      >
        <div ref={messagesEndRef} />

        {messages.length === 0 && (
          <Stack height={'80%'} alignItems={'center'} spacing={4}>
            <Typography color="primary">
              <UserIcon icon="humbleicons:ai" fontSize={52}></UserIcon>
            </Typography>
            <Typography variant="body2" textAlign={'center'} px={2}>
              Assistant that can retrieve user info,
              <br />
              data search and analyze records, <br />
              update or create objects, and generate links.
            </Typography>
          </Stack>
        )}
        {selectedThread && <StreamMessage threadId={selectedThread?.id} />}
        {messages.map((message) => (
          <Stack
            key={message.id}
            justifyContent={'center'}
            width={'100%'}
            px={3}
            alignItems={message.role === 'user' ? 'flex-end' : 'flex-start'}
          >
            {message.role === 'user' && (
              <Stack
                borderRadius={'5px'}
                py={2}
                px={2}
                sx={{
                  wordWrap: 'break-world',
                  backgroundColor: '#EEEDFD',
                }}
                spacing={1}
                maxWidth={'90%'}
                minWidth={'100px'}
              >
                <Typography variant="body2">{message.content}</Typography>

                <Typography variant="caption" textAlign={'right'}>
                  {new Date(message.createdAt).toLocaleTimeString()}
                </Typography>
              </Stack>
            )}
            {message.role === 'assistant' && message.content && (
              <Stack
                borderRadius={'5px'}
                py={2}
                px={2}
                sx={{
                  backgroundColor: 'white',
                }}
                spacing={1}
                maxWidth={'100%'}
                minWidth={'100px'}
              >
                <Stack>
                  {/* { message.parts.map((part)=> (
                    <Chip
                      label={part.type.}
                      onClick={handleClick}
                      onDelete={handleDelete}
                      deleteIcon={<DoneIcon />}
                    />
                  ))} */}
                </Stack>
                <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                  <Markdown
                    children={message.content}
                    markdownCustomStyles={MarkdownStyles}
                  />
                </Typography>

                <Typography variant="caption" textAlign={'right'}>
                  {new Date(message.createdAt).toLocaleTimeString()}
                </Typography>
              </Stack>
            )}
          </Stack>
        ))}
      </Stack>

      {/* Input */}
      <Stack
        px={2}
        pb={'0.6em'}
        position={'absolute'}
        bottom={0}
        width={'100%'}
      >
        <Stack
          direction={'row'}
          spacing={1}
          alignItems={'center'}
          position={'relative'}
        >
          <TextField
            value={inputValue}
            multiline
            fullWidth
            size="small"
            minRows={2}
            maxRows={4}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(5px)',
              borderRadius: '12px',
            }}
            inputProps={{ sx: { mr: '32px' } }}
            onFocus={(event) => event.stopPropagation()}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={
              connectionStatus === 'connected'
                ? 'Ask a question...'
                : connectionStatus === 'connecting'
                ? 'Connection...'
                : 'Something went wrong...'
            }
            // disabled={}
          />
          {selectedThread && (
            <SendButton
              threadId={selectedThread?.id}
              disabled={!inputValue.trim()}
              onSendMessage={handleSendMessage}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}

function clampModalPosition(
  x: number,
  y: number,
  width: number,
  height: number
): { x: number; y: number } {
  const maxX = window.innerWidth - width
  const maxY = window.innerHeight - height

  return {
    x: Math.min(Math.max(0, x), Math.max(0, maxX)),
    y: Math.min(Math.max(0, y), Math.max(0, maxY)),
  }
}

export const AssinstantWindow = () => {
  const elRef = useRef<HTMLDivElement | null>(null)
  if (!elRef.current) {
    elRef.current = document.createElement('div')
  }

  const refContainer = useRef<HTMLDivElement>(null)
  const refDropRightZone = useRef<HTMLDivElement>(null)
  const assinstantWindow = useAssistantSelect.window()
  const updateWindow = useAssistantAction.updateWindow()
  const [pos, setPos] = useState({ x: 100, y: 100 })
  const [size, setSize] = useState({ w: 100, h: 100 })
  const [rightSideBarInit, setRightSideBarInit] = React.useState(
    assinstantWindow.rightSideBar
  )

  React.useEffect(() => {
    if (assinstantWindow.rightSideBar === false) {
      setRightSideBarInit(false)
    } else {
      setTimeout(() => setRightSideBarInit(true), 1000)
    }
  }, [assinstantWindow.rightSideBar])

  const isDropZone = (x: number) =>
    innerWidth - (x + assinstantWindow.width / 2) - 60 < 0
  useEffect(() => {
    const el = elRef.current!
    document.body.appendChild(el)

    return () => {
      document.body.removeChild(el)
    }
  }, [])

  const handelUpdatePosition = (post: { x: number; y: number }) => {
    const adaptivePost = clampModalPosition(
      post.x,
      post.y,
      assinstantWindow.width,
      assinstantWindow.height
    )
    setPos(adaptivePost)
    updateWindow(adaptivePost)
  }
  const handelUpdateSize = (size: { w: number; h: number }) => {
    setSize(size)
    updateWindow({ width: size.w, height: size.h })
  }

  useEffect(() => {
    let timer: number | null = null

    function handleResize() {
      if (timer) clearTimeout(timer)
      timer = window.setTimeout(() => {
        setPos((p) =>
          clampModalPosition(
            p.x,
            p.y,
            assinstantWindow.width,
            assinstantWindow.height
          )
        )
        timer = null
      }, 1000)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
    }
  }, [
    assinstantWindow.width,
    assinstantWindow.height,
    assinstantWindow.x,
    assinstantWindow.y,
  ])

  React.useEffect(() => {
    setPos((p) =>
      clampModalPosition(
        assinstantWindow.x,
        assinstantWindow.y,
        assinstantWindow.width,
        assinstantWindow.height
      )
    )
    setSize({ w: assinstantWindow.width, h: assinstantWindow.height })
  }, [assinstantWindow.open])

  if (!assinstantWindow.open) return null

  if (rightSideBarInit && assinstantWindow.rightSideBar) {
    return createPortal(
      <Stack
        ref={refContainer}
        sx={{
          position: 'fixed',
          zIndex: 10000,
          top: 0,
          right: 0,
          width: '400px',
          pl: 6,
          background: '#F6F6F6',
          py: 6,
          height: '100vh',
        }}
      >
        <Paper
          elevation={12}
          sx={{
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <AssinstantChat />
        </Paper>
      </Stack>,
      elRef.current
    )
  }

  return createPortal(
    <div
      ref={refContainer}
      style={{
        pointerEvents: 'none',
        position: 'fixed',
        zIndex: 10000,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
      }}
    >
      <Stack
        ref={refDropRightZone}
        sx={{
          position: 'fixed',
          right: 0,
          height: '100%',
          width: 0,
          transition: 'all 0.3s ease-in-out',
          border: '1px solid rgba(11,214,141,0.5)',

          background: `linear-gradient(90deg, rgba(11, 214, 141, 0.5) 0%, rgba(11, 214, 141, 1) 80%)`,
          opacity: 0,
        }}
      ></Stack>

      <Draggable
        handle=".assistant-draggable-handle"
        position={{
          x: assinstantWindow.rightSideBar ? innerWidth - 409.1 : pos.x,
          y: assinstantWindow.rightSideBar ? 0 : pos.y,
        }}
        onStart={() => {
          if (refContainer.current) {
            refContainer.current.style.pointerEvents = 'all'
          }
          if (refDropRightZone.current) {
            refDropRightZone.current.style.width = '70px'
            refDropRightZone.current.style.opacity = '0.5'
          }
        }}
        onDrag={(event, { x, y }) => {
          if (refDropRightZone.current) {
            if (isDropZone(x)) {
              refDropRightZone.current.style.opacity = '1'
              if (!assinstantWindow.rightSideBarHover) {
                updateWindow({ rightSideBarHover: true })
              }
            } else {
              refDropRightZone.current.style.opacity = '0.5'
              if (assinstantWindow.rightSideBarHover) {
                updateWindow({ rightSideBarHover: false })
              }
            }
          }
          //  handelUpdatePosition({ x, y })
        }}
        onStop={(event, { x, y }) => {
          handelUpdatePosition({ x, y })
          if (refContainer.current) {
            refContainer.current.style.pointerEvents = 'none'
          }
          if (refDropRightZone.current) {
            if (isDropZone(x)) {
              updateWindow({ rightSideBarHover: false, rightSideBar: true })
            }
            refDropRightZone.current.style.width = '0px'
            refDropRightZone.current.style.opacity = '0'
          }
        }}
      >
        <Stack
          sx={{
            width: assinstantWindow.rightSideBar ? 400 : size.w,
            height: assinstantWindow.rightSideBar ? '100vh' : size.h,
            transition:
              rightSideBarInit !== assinstantWindow.rightSideBar
                ? 'all 0.3s ease-in-out'
                : undefined,
            py: assinstantWindow.rightSideBar ? 6 : 0,
            pl: assinstantWindow.rightSideBar ? 6 : 0,
          }}
        >
          <Paper
            elevation={assinstantWindow.rightSideBar ? 12 : 24}
            sx={{
              pointerEvents: 'all',
              zIndex: 10000,
              border: '1px solid',
              borderColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(255,255,255,0.7)',
              position: 'relative',
              borderBottomRightRadius: assinstantWindow.rightSideBar
                ? '0'
                : undefined,
              borderTopRightRadius: assinstantWindow.rightSideBar
                ? '0'
                : undefined,
              width: assinstantWindow.rightSideBar ? '100%' : size.w,
              height: assinstantWindow.rightSideBar ? '100%' : size.h,
              transition:
                rightSideBarInit !== assinstantWindow.rightSideBar
                  ? 'all 0.3s ease-in-out'
                  : undefined,
            }}
          >
            {assinstantWindow.rightSideBar ? (
              <AssinstantChat />
            ) : (
              <ResizableBox
                width={size.w}
                height={size.h}
                minConstraints={[350, 450]}
                onResizeStart={(e, { size }) => {
                  handelUpdateSize({ w: size.width, h: size.height })
                  if (refContainer.current) {
                    refContainer.current.style.pointerEvents = 'all'
                  }
                }}
                onResizeStop={(e, { size }) => {
                  handelUpdateSize({ w: size.width, h: size.height })
                  if (refContainer.current) {
                    refContainer.current.style.pointerEvents = 'none'
                  }
                }}
                onResize={(e, { size }) =>
                  handelUpdateSize({ w: size.width, h: size.height })
                }
                handle={
                  <Stack
                    position={'absolute'}
                    bottom={4}
                    right={4}
                    color="grey.600"
                    sx={{ cursor: 'nw-resize' }}
                  >
                    <UserIcon icon="lets-icons:resize-down-right-light" />
                  </Stack>
                }
              >
                <AssinstantChat />
              </ResizableBox>
            )}
          </Paper>
        </Stack>
      </Draggable>
    </div>,
    elRef.current
  )
}
